import type { FC, ReactNode } from 'react'

const searchTips: {
    title: string
    example: string
    description: ReactNode
}[] = [
    {
        title: 'Single keyword',
        example: 'cloud',
        description: 'finds profiles mentioning "cloud" in any field.',
    },
    {
        title: 'Multiple terms',
        example: 'cloud aws gcp azure devops',
        description: (
            <>
                matches only profiles that include <em>all</em> of these terms
                in any field. Effectively adding{' '}
                <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                    AND
                </code>{' '}
                between them.
            </>
        ),
    },
    {
        title: 'Exact phrase',
        example: '"software engineer"',
        description:
            "matches the exact phrase in order, even if it's part of a longer sentence.",
    },
    {
        title: 'Field-specific search',
        example: 'headline:developer',
        description: (
            <>
                or{' '}
                <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                    summary:"data engineer"
                </code>
            </>
        ),
    },
    {
        title: 'Combine terms',
        example: 'cloud AND AWS',
        description: (
            <>
                must match both. Use{' '}
                <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                    OR
                </code>{' '}
                for either.
            </>
        ),
    },
    {
        title: 'Grouped search',
        example: 'skills:(AWS OR GCP)',
        description: 'matches if any listed skill is present.',
    },
    {
        title: 'Fuzzy match',
        example: 'develop~',
        description: 'matches similar terms like "developer" or "development".',
    },
    {
        title: 'Boost relevance',
        example: 'cloud^3',
        description: 'increases importance of "cloud" in ranking results.',
    },
    {
        title: 'Exclude terms',
        example: 'cloud NOT intern',
        description:
            'includes "cloud" but excludes results mentioning "intern".',
    },
]

const availableFields: { name: string; note?: string }[] = [
    { name: 'firstName' },
    { name: 'lastName' },
    { name: 'fullName' },
    { name: 'linkedInUrl' },
    { name: 'headline' },
    { name: 'summary' },
    { name: 'skills' },
    { name: 'location' },
    { name: 'industry' },
    {
        name: 'locationCountry',
        note: 'search by country (e.g. "United Kingdom")',
    },
    { name: 'jobDescription' },
    {
        name: 'jobTitle',
        note: 'all job titles (past and current)',
    },
    {
        name: 'company',
        note: 'all companies (past and current)',
    },
    { name: 'currentJobTitle' },
    { name: 'currentCompany' },
    { name: 'pastJobTitle' },
    { name: 'pastCompany' },
    {
        name: 'isDecisionMaker',
        note: 'use true/false, e.g. isDecisionMaker:true',
    },
    { name: 'department' },
    { name: 'managementLevel' },
]

const BooleanSearchHelpContent: FC = () => {
    return (
        <>
            <p className="font-semibold">How to search:</p>
            <div className="space-y-2 pl-2 text-sm text-gray-800">
                {searchTips
                    .slice(0, 4)
                    .map(({ title, example, description }) => (
                        <div key={title}>
                            <span className="font-semibold">{title}:</span>{' '}
                            <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                {example}
                            </code>{' '}
                            {description}
                        </div>
                    ))}
                <div className="pl-2">
                    <p className="font-semibold mt-2">Available fields:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-800">
                        {availableFields.map(({ name, note }) => (
                            <li key={name}>
                                <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                    {name}
                                </code>
                                {note ? (
                                    <span className="ml-2">{note}</span>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                </div>
                {searchTips.slice(4).map(({ title, example, description }) => (
                    <div key={title}>
                        <span className="font-semibold">{title}:</span>{' '}
                        <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                            {example}
                        </code>{' '}
                        {description}
                    </div>
                ))}
            </div>
        </>
    )
}

export default BooleanSearchHelpContent
